import { UI } from './ui.js';
import { YuNetDetector } from './detector/index.js';
import { createBlurrer } from './blurrer/index.js';
import { Pipeline } from './pipeline.js';
import { VideoReader, VideoWriter } from './utils/video.js';
import { fillDetectionGaps, applyPersistence } from './utils/smooth-detections.js';
import cfg from '../config.json';

const MODEL_PATH = '/models/face_detection_yunet_2023mar.onnx';
const DEV = true; // draw red debug boxes on the preview canvas

let ui, detector, blurrer, blurBackend;
let currentPipeline = null;
let pendingFile     = null;
let pendingReader   = null;

async function init() {
  ui = new UI();

ui.setStatus('Loading face detection model…', { loading: true });

  try {
    [detector, { blurrer, backend: blurBackend }] = await Promise.all([
      YuNetDetector.create(MODEL_PATH, { useWebGPU: false }),
      createBlurrer(),
    ]);
  } catch (err) {
    ui.setStatus(
      err.message.includes('404') || err.message.includes('fetch')
        ? 'Model not found. Run: yarn download-model'
        : `Init error: ${err.message}`,
      { error: true },
    );
    return;
  }

  ui.setStatus(
    `Ready · ONNX/WASM · Blur: ${blurBackend === 'webgpu' ? 'WebGPU' : 'Canvas 2D'}`,
  );

  ui.onFileSelected(loadFile);
  ui.onStart(() => { if (pendingFile) startProcessing(pendingFile); });
  ui.onNewVideo(() => {
    currentPipeline?.abort();
    pendingFile = null;
    ui.showDropzone();
  });
}

async function loadFile(file) {
  pendingFile = file;
  const reader = new VideoReader();

  ui.setStatus('Reading video…', { loading: true });
  try {
    await reader.load(file);
  } catch (err) {
    ui.setStatus(`Cannot read video: ${err.message}`, { error: true });
    return;
  }

  pendingReader = reader;
  ui.showConfirmStep(reader.width, reader.height);

  // Show first frame as preview
  const firstFrame = await reader.getFrameAt(0);
  ui.renderFrame(firstFrame);
  ui.setStatus(`${reader.width}×${reader.height} · ${reader.fps} fps · ${reader.frameCount} frames`);
}

async function startProcessing(file) {
  const reader = pendingReader;
  pendingFile = null; pendingReader = null;

  ui.startProcessing();
  const { width, height, fps } = reader;

  const settings = ui.settings;
  currentPipeline = new Pipeline(detector, blurrer, settings);

  // ── Pass 1: detect faces frame by frame (no blur, no recording) ──────────
  const detectionMap = new Map(); // frameIndex → Detection[]

  ui.setStatus('1/2 — detecting faces…', { loading: true });

  try {
    for await (const { detections, index, total, msDetect }
      of currentPipeline.detectOnly(reader)) {
      detectionMap.set(index, detections);
      ui.updateProgress(index + 1, total, detections.length, msDetect, 0);
    }
  } catch (err) {
    ui.setStatus(`Detection error: ${err.message}`, { error: true });
    console.error(err);
    return;
  }

  // Fill temporal detection gaps (interpolate boxes across missed frames)
  const gappedMap   = fillDetectionGaps(detectionMap, reader.frameCount, settings.maxGapFrames,    settings.gapFillMinScore);
  const smoothedMap = applyPersistence(gappedMap,    reader.frameCount, settings.persistenceFrames, settings.persistenceMinScore);

  // ── Pass 2: real-time playback → blur → record canvas + audio ────────────
  const writerCanvas = document.createElement('canvas');
  writerCanvas.width  = width;
  writerCanvas.height = height;

  // Pass reader.video so VideoWriter can grab its audio track via captureStream()
  const writer = new VideoWriter(writerCanvas, fps, reader.video);
  await writer.start();

  ui.setStatus('2/2 — rendering with audio…', { loading: true });

  try {
    await reader.playAndRender(
      smoothedMap,
      blurrer,
      settings,
      (frame, detections) => {
        writer.writeFrame(frame);   // recorded output — no debug boxes
        ui.renderFrame(frame);      // preview canvas
        if (DEV) drawDebugBoxes(ui.outputCanvas, detections);
      },
      (index, total) => {
        const faces = smoothedMap.get(index)?.length ?? 0;
        ui.updateProgress(index + 1, total, faces, 0, 0);
      },
    );
  } catch (err) {
    ui.setStatus(`Render error: ${err.message}`, { error: true });
    console.error(err);
    return;
  }

  const outputUrl = await writer.stop();
  const outName   = file.name.replace(/\.[^.]+$/, '') + '_maskr.webm';

  ui.enableDownload(outputUrl, outName);
  ui.setStatus(
    `Done · ${detectionMap.size} frames · ${blurBackend === 'webgpu' ? 'WebGPU' : 'Canvas 2D'} blur`,
  );

  // ── Review mode ─────────────────────────────────────────────────────────
  ui.showReviewPanel(reader.frameCount);

  let reviewing = false;
  ui.onScrub(async frameIndex => {
    if (reviewing) return;
    reviewing = true;
    try {
      const raw        = await reader.getFrameAt(frameIndex);
      const detections = smoothedMap.get(frameIndex) ?? [];
      const processed  = detections.length > 0
        ? await blurrer.blurFaces(raw, detections, settings)
        : raw;
      ui.renderFrame(processed);
      drawDebugBoxes(ui.outputCanvas, detections);
      ui.updateDetectionInfo(frameIndex, detections);
    } finally {
      reviewing = false;
    }
  });
}

const GHOST_STYLE = {
  gap:     { color: '#e09855', dash: [4, 3], width: 1.5, label: '~' },
  persist: { color: '#8b5cf6', dash: [2, 4], width: 1.5, label: '↔' },
};

function drawDebugBoxes(canvas, detections) {
  if (!detections.length) return;
  const ctx = canvas.getContext('2d');
  ctx.save();
  ctx.font = 'bold 11px monospace';
  for (const { x1, y1, x2, y2, score, ghost } of detections) {
    const style = ghost ? (GHOST_STYLE[ghost] ?? GHOST_STYLE.gap) : null;
    ctx.strokeStyle = style?.color ?? '#ff3333';
    ctx.fillStyle   = style?.color ?? '#ff3333';
    ctx.lineWidth   = style?.width ?? 2;
    ctx.setLineDash(style?.dash   ?? []);
    ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
    const tag = style ? ` ${style.label}` : '';
    ctx.fillText(`${(score * 100).toFixed(0)}%${tag}`, x1 + 3, y1 + 13);
  }
  ctx.setLineDash([]);
  ctx.restore();
}

init();
